/* */ 
(function(process) {
  module.exports = AlgoliaSearchCore;
  var errors = require('./errors');
  var exitPromise = require('./exitPromise');
  var IndexCore = require('./IndexCore');
  var store = require('./store');
  var MAX_API_KEY_LENGTH = 500;
  var RESET_APP_DATA_TIMER = process.env.RESET_APP_DATA_TIMER && parseInt(process.env.RESET_APP_DATA_TIMER, 10) || 60 * 2 * 1000;
  function AlgoliaSearchCore(applicationID, apiKey, opts) {
    var debug = require('debug')('algoliasearch');
    var clone = require('./clone');
    var isArray = require('isarray');
    var map = require('./map');
    var usage = 'Usage: algoliasearch(applicationID, apiKey, opts)';
    if (opts._allowEmptyCredentials !== true && !applicationID) {
      throw new errors.AlgoliaSearchError('Please provide an application ID. ' + usage);
    }
    if (opts._allowEmptyCredentials !== true && !apiKey) {
      throw new errors.AlgoliaSearchError('Please provide an API key. ' + usage);
    }
    this.applicationID = applicationID;
    this.apiKey = apiKey;
    this.hosts = {
      read: [],
      write: []
    };
    opts = opts || {};
    var protocol = opts.protocol || 'https:';
    this._timeouts = opts.timeouts || {
      connect: 1 * 1000,
      read: 2 * 1000,
      write: 30 * 1000
    };
    if (opts.timeout) {
      this._timeouts.connect = this._timeouts.read = this._timeouts.write = opts.timeout;
    }
    if (!/:$/.test(protocol)) {
      protocol = protocol + ':';
    }
    if (opts.protocol !== 'http:' && opts.protocol !== 'https:') {
      throw new errors.AlgoliaSearchError('protocol must be `http:` or `https:` (was `' + opts.protocol + '`)');
    }
    this._checkAppIdData();
    if (!opts.hosts) {
      var defaultHosts = map(this._shuffleResult, function(hostNumber) {
        return applicationID + '-' + hostNumber + '.algolianet.com';
      });
      this.hosts.read = [this.applicationID + '-dsn.algolia.net'].concat(defaultHosts);
      this.hosts.write = [this.applicationID + '.algolia.net'].concat(defaultHosts);
    } else if (isArray(opts.hosts)) {
      this.hosts.read = clone(opts.hosts);
      this.hosts.write = clone(opts.hosts);
    } else {
      this.hosts.read = clone(opts.hosts.read);
      this.hosts.write = clone(opts.hosts.write);
    }
    this.hosts.read = map(this.hosts.read, prepareHost(protocol));
    this.hosts.write = map(this.hosts.write, prepareHost(protocol));
    this.extraHeaders = [];
    this.cache = opts._cache || {};
    this._ua = opts._ua;
    this._useCache = opts._useCache === undefined || opts._cache ? true : opts._useCache;
    this._useFallback = opts.useFallback === undefined ? true : opts.useFallback;
    this._setTimeout = opts._setTimeout;
    debug('init done, %j', this);
  }
  AlgoliaSearchCore.prototype.initIndex = function(indexName) {
    return new IndexCore(this, indexName);
  };
  AlgoliaSearchCore.prototype.setExtraHeader = function(name, value) {
    this.extraHeaders.push({
      name: name.toLowerCase(),
      value: value
    });
  };
  AlgoliaSearchCore.prototype.addAlgoliaAgent = function(algoliaAgent) {
    if (this._ua.indexOf(';' + algoliaAgent) === -1) {
      this._ua += ';' + algoliaAgent;
    }
  };
  AlgoliaSearchCore.prototype._jsonRequest = function(initialOpts) {
    this._checkAppIdData();
    var requestDebug = require('debug')('algoliasearch:' + initialOpts.url);
    var body;
    var additionalUA = initialOpts.additionalUA || '';
    var cache = initialOpts.cache;
    var client = this;
    var tries = 0;
    var usingFallback = false;
    var hasFallback = client._useFallback && client._request.fallback && initialOpts.fallback;
    var headers;
    if (this.apiKey.length > MAX_API_KEY_LENGTH && initialOpts.body !== undefined && (initialOpts.body.params !== undefined || initialOpts.body.requests !== undefined)) {
      initialOpts.body.apiKey = this.apiKey;
      headers = this._computeRequestHeaders(additionalUA, false);
    } else {
      headers = this._computeRequestHeaders(additionalUA);
    }
    if (initialOpts.body !== undefined) {
      body = safeJSONStringify(initialOpts.body);
    }
    requestDebug('request start');
    var debugData = [];
    function doRequest(requester, reqOpts) {
      client._checkAppIdData();
      var startTime = new Date();
      var cacheID;
      if (client._useCache) {
        cacheID = initialOpts.url;
      }
      if (client._useCache && body) {
        cacheID += '_body_' + reqOpts.body;
      }
      if (client._useCache && cache && cache[cacheID] !== undefined) {
        requestDebug('serving response from cache');
        return client._promise.resolve(JSON.parse(cache[cacheID]));
      }
      if (tries >= client.hosts[initialOpts.hostType].length) {
        if (!hasFallback || usingFallback) {
          requestDebug('could not get any response');
          return client._promise.reject(new errors.AlgoliaSearchError('Cannot connect to the AlgoliaSearch API.' + ' Send an email to support@algolia.com to report and resolve the issue.' + ' Application id was: ' + client.applicationID, {debugData: debugData}));
        }
        requestDebug('switching to fallback');
        tries = 0;
        reqOpts.method = initialOpts.fallback.method;
        reqOpts.url = initialOpts.fallback.url;
        reqOpts.jsonBody = initialOpts.fallback.body;
        if (reqOpts.jsonBody) {
          reqOpts.body = safeJSONStringify(reqOpts.jsonBody);
        }
        headers = client._computeRequestHeaders(additionalUA);
        reqOpts.timeouts = client._getTimeoutsForRequest(initialOpts.hostType);
        client._setHostIndexByType(0, initialOpts.hostType);
        usingFallback = true;
        return doRequest(client._request.fallback, reqOpts);
      }
      var currentHost = client._getHostByType(initialOpts.hostType);
      var url = currentHost + reqOpts.url;
      var options = {
        body: reqOpts.body,
        jsonBody: reqOpts.jsonBody,
        method: reqOpts.method,
        headers: headers,
        timeouts: reqOpts.timeouts,
        debug: requestDebug
      };
      requestDebug('method: %s, url: %s, headers: %j, timeouts: %d', options.method, url, options.headers, options.timeouts);
      if (requester === client._request.fallback) {
        requestDebug('using fallback');
      }
      return requester.call(client, url, options).then(success, tryFallback);
      function success(httpResponse) {
        var status = httpResponse && httpResponse.body && httpResponse.body.message && httpResponse.body.status || httpResponse.statusCode || httpResponse && httpResponse.body && 200;
        requestDebug('received response: statusCode: %s, computed statusCode: %d, headers: %j', httpResponse.statusCode, status, httpResponse.headers);
        var httpResponseOk = Math.floor(status / 100) === 2;
        var endTime = new Date();
        debugData.push({
          currentHost: currentHost,
          headers: removeCredentials(headers),
          content: body || null,
          contentLength: body !== undefined ? body.length : null,
          method: reqOpts.method,
          timeouts: reqOpts.timeouts,
          url: reqOpts.url,
          startTime: startTime,
          endTime: endTime,
          duration: endTime - startTime,
          statusCode: status
        });
        if (httpResponseOk) {
          if (client._useCache && cache) {
            cache[cacheID] = httpResponse.responseText;
          }
          return httpResponse.body;
        }
        var shouldRetry = Math.floor(status / 100) !== 4;
        if (shouldRetry) {
          tries += 1;
          return retryRequest();
        }
        requestDebug('unrecoverable error');
        var unrecoverableError = new errors.AlgoliaSearchError(httpResponse.body && httpResponse.body.message, {
          debugData: debugData,
          statusCode: status
        });
        return client._promise.reject(unrecoverableError);
      }
      function tryFallback(err) {
        requestDebug('error: %s, stack: %s', err.message, err.stack);
        var endTime = new Date();
        debugData.push({
          currentHost: currentHost,
          headers: removeCredentials(headers),
          content: body || null,
          contentLength: body !== undefined ? body.length : null,
          method: reqOpts.method,
          timeouts: reqOpts.timeouts,
          url: reqOpts.url,
          startTime: startTime,
          endTime: endTime,
          duration: endTime - startTime
        });
        if (!(err instanceof errors.AlgoliaSearchError)) {
          err = new errors.Unknown(err && err.message, err);
        }
        tries += 1;
        if (err instanceof errors.Unknown || err instanceof errors.UnparsableJSON || tries >= client.hosts[initialOpts.hostType].length && (usingFallback || !hasFallback)) {
          err.debugData = debugData;
          return client._promise.reject(err);
        }
        if (err instanceof errors.RequestTimeout) {
          return retryRequestWithHigherTimeout();
        }
        return retryRequest();
      }
      function retryRequest() {
        requestDebug('retrying request');
        client._incrementHostIndex(initialOpts.hostType);
        return doRequest(requester, reqOpts);
      }
      function retryRequestWithHigherTimeout() {
        requestDebug('retrying request with higher timeout');
        client._incrementHostIndex(initialOpts.hostType);
        client._incrementTimeoutMultipler();
        reqOpts.timeouts = client._getTimeoutsForRequest(initialOpts.hostType);
        return doRequest(requester, reqOpts);
      }
    }
    var promise = doRequest(client._request, {
      url: initialOpts.url,
      method: initialOpts.method,
      body: body,
      jsonBody: initialOpts.body,
      timeouts: client._getTimeoutsForRequest(initialOpts.hostType)
    });
    if (initialOpts.callback) {
      promise.then(function okCb(content) {
        exitPromise(function() {
          initialOpts.callback(null, content);
        }, client._setTimeout || setTimeout);
      }, function nookCb(err) {
        exitPromise(function() {
          initialOpts.callback(err);
        }, client._setTimeout || setTimeout);
      });
    } else {
      return promise;
    }
  };
  AlgoliaSearchCore.prototype._getSearchParams = function(args, params) {
    if (args === undefined || args === null) {
      return params;
    }
    for (var key in args) {
      if (key !== null && args[key] !== undefined && args.hasOwnProperty(key)) {
        params += params === '' ? '' : '&';
        params += key + '=' + encodeURIComponent(Object.prototype.toString.call(args[key]) === '[object Array]' ? safeJSONStringify(args[key]) : args[key]);
      }
    }
    return params;
  };
  AlgoliaSearchCore.prototype._computeRequestHeaders = function(additionalUA, withAPIKey) {
    var forEach = require('foreach');
    var ua = additionalUA ? this._ua + ';' + additionalUA : this._ua;
    var requestHeaders = {
      'x-algolia-agent': ua,
      'x-algolia-application-id': this.applicationID
    };
    if (withAPIKey !== false) {
      requestHeaders['x-algolia-api-key'] = this.apiKey;
    }
    if (this.userToken) {
      requestHeaders['x-algolia-usertoken'] = this.userToken;
    }
    if (this.securityTags) {
      requestHeaders['x-algolia-tagfilters'] = this.securityTags;
    }
    if (this.extraHeaders) {
      forEach(this.extraHeaders, function addToRequestHeaders(header) {
        requestHeaders[header.name] = header.value;
      });
    }
    return requestHeaders;
  };
  AlgoliaSearchCore.prototype.search = function(queries, opts, callback) {
    var isArray = require('isarray');
    var map = require('./map');
    var usage = 'Usage: client.search(arrayOfQueries[, callback])';
    if (!isArray(queries)) {
      throw new Error(usage);
    }
    if (typeof opts === 'function') {
      callback = opts;
      opts = {};
    } else if (opts === undefined) {
      opts = {};
    }
    var client = this;
    var postObj = {requests: map(queries, function prepareRequest(query) {
        var params = '';
        if (query.query !== undefined) {
          params += 'query=' + encodeURIComponent(query.query);
        }
        return {
          indexName: query.indexName,
          params: client._getSearchParams(query.params, params)
        };
      })};
    var JSONPParams = map(postObj.requests, function prepareJSONPParams(request, requestId) {
      return requestId + '=' + encodeURIComponent('/1/indexes/' + encodeURIComponent(request.indexName) + '?' + request.params);
    }).join('&');
    var url = '/1/indexes/*/queries';
    if (opts.strategy !== undefined) {
      url += '?strategy=' + opts.strategy;
    }
    return this._jsonRequest({
      cache: this.cache,
      method: 'POST',
      url: url,
      body: postObj,
      hostType: 'read',
      fallback: {
        method: 'GET',
        url: '/1/indexes/*',
        body: {params: JSONPParams}
      },
      callback: callback
    });
  };
  AlgoliaSearchCore.prototype.setSecurityTags = function(tags) {
    if (Object.prototype.toString.call(tags) === '[object Array]') {
      var strTags = [];
      for (var i = 0; i < tags.length; ++i) {
        if (Object.prototype.toString.call(tags[i]) === '[object Array]') {
          var oredTags = [];
          for (var j = 0; j < tags[i].length; ++j) {
            oredTags.push(tags[i][j]);
          }
          strTags.push('(' + oredTags.join(',') + ')');
        } else {
          strTags.push(tags[i]);
        }
      }
      tags = strTags.join(',');
    }
    this.securityTags = tags;
  };
  AlgoliaSearchCore.prototype.setUserToken = function(userToken) {
    this.userToken = userToken;
  };
  AlgoliaSearchCore.prototype.clearCache = function() {
    this.cache = {};
  };
  AlgoliaSearchCore.prototype.setRequestTimeout = function(milliseconds) {
    if (milliseconds) {
      this._timeouts.connect = this._timeouts.read = this._timeouts.write = milliseconds;
    }
  };
  AlgoliaSearchCore.prototype.setTimeouts = function(timeouts) {
    this._timeouts = timeouts;
  };
  AlgoliaSearchCore.prototype.getTimeouts = function() {
    return this._timeouts;
  };
  AlgoliaSearchCore.prototype._getAppIdData = function() {
    var data = store.get(this.applicationID);
    if (data !== null)
      this._cacheAppIdData(data);
    return data;
  };
  AlgoliaSearchCore.prototype._setAppIdData = function(data) {
    data.lastChange = (new Date()).getTime();
    this._cacheAppIdData(data);
    return store.set(this.applicationID, data);
  };
  AlgoliaSearchCore.prototype._checkAppIdData = function() {
    var data = this._getAppIdData();
    var now = (new Date()).getTime();
    if (data === null || now - data.lastChange > RESET_APP_DATA_TIMER) {
      return this._resetInitialAppIdData(data);
    }
    return data;
  };
  AlgoliaSearchCore.prototype._resetInitialAppIdData = function(data) {
    var newData = data || {};
    newData.hostIndexes = {
      read: 0,
      write: 0
    };
    newData.timeoutMultiplier = 1;
    newData.shuffleResult = newData.shuffleResult || shuffle([1, 2, 3]);
    return this._setAppIdData(newData);
  };
  AlgoliaSearchCore.prototype._cacheAppIdData = function(data) {
    this._hostIndexes = data.hostIndexes;
    this._timeoutMultiplier = data.timeoutMultiplier;
    this._shuffleResult = data.shuffleResult;
  };
  AlgoliaSearchCore.prototype._partialAppIdDataUpdate = function(newData) {
    var foreach = require('foreach');
    var currentData = this._getAppIdData();
    foreach(newData, function(value, key) {
      currentData[key] = value;
    });
    return this._setAppIdData(currentData);
  };
  AlgoliaSearchCore.prototype._getHostByType = function(hostType) {
    return this.hosts[hostType][this._getHostIndexByType(hostType)];
  };
  AlgoliaSearchCore.prototype._getTimeoutMultiplier = function() {
    return this._timeoutMultiplier;
  };
  AlgoliaSearchCore.prototype._getHostIndexByType = function(hostType) {
    return this._hostIndexes[hostType];
  };
  AlgoliaSearchCore.prototype._setHostIndexByType = function(hostIndex, hostType) {
    var clone = require('./clone');
    var newHostIndexes = clone(this._hostIndexes);
    newHostIndexes[hostType] = hostIndex;
    this._partialAppIdDataUpdate({hostIndexes: newHostIndexes});
    return hostIndex;
  };
  AlgoliaSearchCore.prototype._incrementHostIndex = function(hostType) {
    return this._setHostIndexByType((this._getHostIndexByType(hostType) + 1) % this.hosts[hostType].length, hostType);
  };
  AlgoliaSearchCore.prototype._incrementTimeoutMultipler = function() {
    var timeoutMultiplier = Math.max(this._timeoutMultiplier + 1, 4);
    return this._partialAppIdDataUpdate({timeoutMultiplier: timeoutMultiplier});
  };
  AlgoliaSearchCore.prototype._getTimeoutsForRequest = function(hostType) {
    return {
      connect: this._timeouts.connect * this._timeoutMultiplier,
      complete: this._timeouts[hostType] * this._timeoutMultiplier
    };
  };
  function prepareHost(protocol) {
    return function prepare(host) {
      return protocol + '//' + host.toLowerCase();
    };
  }
  function safeJSONStringify(obj) {
    if (Array.prototype.toJSON === undefined) {
      return JSON.stringify(obj);
    }
    var toJSON = Array.prototype.toJSON;
    delete Array.prototype.toJSON;
    var out = JSON.stringify(obj);
    Array.prototype.toJSON = toJSON;
    return out;
  }
  function shuffle(array) {
    var currentIndex = array.length;
    var temporaryValue;
    var randomIndex;
    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;
      temporaryValue = array[currentIndex];
      array[currentIndex] = array[randomIndex];
      array[randomIndex] = temporaryValue;
    }
    return array;
  }
  function removeCredentials(headers) {
    var newHeaders = {};
    for (var headerName in headers) {
      if (Object.prototype.hasOwnProperty.call(headers, headerName)) {
        var value;
        if (headerName === 'x-algolia-api-key' || headerName === 'x-algolia-application-id') {
          value = '**hidden for security purposes**';
        } else {
          value = headers[headerName];
        }
        newHeaders[headerName] = value;
      }
    }
    return newHeaders;
  }
})(require('process'));
